/**
 * Forgiving text matching for the app's in-page search boxes.
 *
 * A raw substring match asks the person to reproduce a name exactly, separators and all — so in a
 * vault of folders called "APPROVED_TF.18.01_Instruction For Use" a search for "TF 18" or
 * "instruction use" finds nothing, while the name is plainly right there. Both sides are
 * normalised (case, punctuation, separators, whitespace) and every word of the query must appear
 * somewhere, in any order.
 *
 * Written for the spec editor's section search first; lifted here unchanged when the vault's
 * folder search needed the same behaviour, so the two can never drift into disagreeing about what
 * a query means.
 */

/** Markup characters that carry no meaning for a search. */
const MARKUP = /[#*_`~|>\\[\]()<>"'“”‘’]/g;

/** Lower-case, punctuation and separators → spaces, whitespace collapsed. */
export function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(MARKUP, ' ')
    .replace(/[-–—/:;,.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Query words (normalised the same way); an empty query means "no filter". */
export function searchTerms(query: string): string[] {
  return normalizeForSearch(query).split(' ').filter(Boolean);
}

/** True when EVERY query word occurs somewhere in `haystack`. An empty query matches everything,
 *  so a caller can pass the box's value straight in. */
export function matchesAllTerms(haystack: string, query: string): boolean {
  const terms = searchTerms(query);
  if (terms.length === 0) return true;
  const hay = normalizeForSearch(haystack);
  return terms.every((t) => hay.includes(t));
}

/**
 * How well `name` answers `query`, lower is better — for ordering a list of matches.
 *
 * Matching alone is not enough when a query matches dozens of names: sorted alphabetically, the
 * folder literally called "StethoMe AI" sits below a dozen deep folders whose names merely
 * contain it, and the person types the whole name to dig it out. Four tiers, then shortest
 * first: a short name containing the query is more likely to BE the thing than a long one that
 * mentions it in passing.
 */
export function matchRank(name: string, query: string): number {
  const hay = normalizeForSearch(name);
  const q = normalizeForSearch(query);
  if (!q) return 3;
  if (hay === q) return 0;                                   // it is exactly this
  if (hay.startsWith(q)) return 1;                           // it begins with it
  if (hay.split(' ').some((w) => w.startsWith(q))) return 2;  // a word in it begins with it
  return 3;                                                   // it is in there somewhere
}
